/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { FulfillmentOption } from '@/types/reduxStore/skuDetails';
import styles from './styles.module.scss';
import cx from 'classnames';
import { useLabel, useLabels } from '@/hooks/useLabels';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import {
  checkIfDateIsTomorrow,
  getTomorrowOrDate,
} from '@/features/shelf/utils/checkIfDateIsTomorrow';
import { createFreeGroundDateRange } from '@/features/shelf/utils/createFreeGroundDateRange';

export function StandardOption({
  option,
  nddAvailable,
}: {
  option: FulfillmentOption;
  nddAvailable: boolean | undefined;
}) {
  const estDeliveryStrings: string[] = option.estimatedDeliveryDate?.split(':');
  const inStockLabel = useLabel('label_cart_lineItem_InStock');
  const labels = useLabels({
    lblTomorrow: 'label_fulfilment_options_tomorrow',
  });
  const isVdpDsUniqueDisplayEnabled = useForteDecisionFlagWithTrueDefault(
    'enableVdpDsUniqueDisplay',
  );
  const showVdpDsAsRange = useForteDecisionFlagWithTrueDefault('showVdpDsAsRange');
  const dateIsARange =
    isVdpDsUniqueDisplayEnabled && showVdpDsAsRange && option.fulfillmentTypeIdOriginal === 103;

  if (option.fulfillmentTypeLabel || option.estimatedDeliveryDate) {
    return (
      <>
        {!nddAvailable ? (
          <div className={cx(styles.listItemContainer)} data-testid="standard-delivery">
            <div className={styles.inStockLabel}>
              <div className={styles.inStockLabelContainer}>
                <div className={styles.tickmarkContainer}>
                  <img src="/images/green-tickmark.svg" alt="" />{' '}
                </div>
                <span>
                  <span className={styles.successBoldedMessage}>{inStockLabel}</span>
                  <span> | </span>
                  {estDeliveryStrings && (
                    <span data-testid="availability" className={styles.shippingOptionDetailV3}>
                      <span
                        className={styles.estDeliveryText}
                        data-testid="estimated-delivery-standard"
                      >
                        {estDeliveryStrings[0]}
                      </span>{' '}
                      <span
                        data-testid="estimated-delivery-date-standard"
                        className={cx(styles.subTextBold, {
                          [styles.deliveryMessageCapitalized]: !checkIfDateIsTomorrow(
                            estDeliveryStrings[1],
                          ),
                        })}
                      >
                        {dateIsARange
                          ? createFreeGroundDateRange(estDeliveryStrings[1])
                          : getTomorrowOrDate(
                              estDeliveryStrings[1]?.replace(/\b0/g, ''),
                              labels.lblTomorrow,
                            )}
                      </span>
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return null;
}
